import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Screen from '../components/ui/Screen'
import BottomNav from '../components/ui/BottomNav'
import Panel from '../components/ui/Panel'
import Chip from '../components/ui/Chip'
import Avatar from '../components/ui/Avatar'
import Button from '../components/ui/Button'
import { useToast } from '../components/ui/Toast'
import { api } from '../lib/api'
import { getSettings } from '../lib/settings'
import { useOnline } from '../lib/online'
import texts from '../texts'
import type { BidType, TrumpSuit } from '../../../shared/types'
import {
  LEVELS,
  VARIANTS,
  SOLO,
  SUITS,
  sittingOut,
  dealer,
  distribute,
  toBidType,
  fromBidType,
  inputFromRound
} from '../lib/scoring'
import type { Level, Variant, Solo, Player, RoundInput } from '../lib/scoring'
import GameCurve from '../components/charts/GameCurve'
import ShareButton from '../components/ShareButton'
import CsvButton from '../components/CsvButton'
import { gameExportCsvUrl } from '../lib/api.share'
import shareTexts from '../texts.share'

const POLL_INTERVAL_MS = 5000

interface GamePlayerApi {
  userId: number
  name: string
  position: number
  totalScore: number
}

interface RoundApi {
  id: number
  roundNumber: number
  dealerId: number
  bidderId: number
  partnerId?: number
  bidType: BidType
  trumpSuit?: TrumpSuit
  tricksTaken: number
  won?: boolean
  score: number
  /** Gemt lokalt mens enheden er offline, endnu ikke bekræftet af serveren. */
  pending?: boolean
}

interface GameApi {
  id: number
  name: string
  status: 'active' | 'completed'
  createdBy: number
  players: GamePlayerApi[]
  rounds: RoundApi[]
}

type View = 'panel' | 'history' | 'board'

interface RoundDraft {
  bidderId: number
  partnerId: number | null
  level: Level
  variant: Variant
  trumpSuit: TrumpSuit
  tricks: number
  solo: Solo | null
  won: boolean
}

function fmt(n: number): string {
  return (n > 0 ? '+' : '') + n
}

function cls(n: number): string {
  return n > 0 ? 'text-mint' : n < 0 ? 'text-red' : 'text-text-2'
}

function freshDraft(players: Player[], roundIndex: number): RoundDraft {
  const out = sittingOut(players, roundIndex)
  const first = players.find((p) => !out.includes(p.userId)) ?? players[0]
  return {
    bidderId: first.userId,
    partnerId: null,
    level: 7,
    variant: 'Almindelige',
    trumpSuit: 'Hjerter',
    tricks: 7,
    solo: null,
    won: true
  }
}

function draftFromRound(round: RoundApi): RoundDraft {
  const { level, variant, solo } = fromBidType(round.bidType)
  return {
    bidderId: round.bidderId,
    partnerId: round.partnerId ?? null,
    level: level ?? 7,
    variant: variant ?? 'Almindelige',
    trumpSuit: round.trumpSuit ?? 'Hjerter',
    tricks: round.tricksTaken,
    solo: solo ?? null,
    won: round.won ?? true
  }
}

function draftToInput(draft: RoundDraft): RoundInput {
  if (draft.solo) return { bidderId: draft.bidderId, solo: draft.solo, won: draft.won }
  return {
    bidderId: draft.bidderId,
    partnerId: draft.partnerId ?? undefined,
    level: draft.level,
    variant: draft.variant,
    tricks: draft.tricks,
    trump: draft.trumpSuit
  }
}

function playSaveSound(): void {
  try {
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    const ctx = new AudioContextClass()
    const oscillator = ctx.createOscillator()
    const gain = ctx.createGain()
    oscillator.frequency.value = 880
    gain.gain.value = 0.05
    oscillator.connect(gain)
    gain.connect(ctx.destination)
    oscillator.start()
    oscillator.stop(ctx.currentTime + 0.06)
    oscillator.onended = () => ctx.close()
  } catch {
    // Lyd er ikke kritisk for at kunne gemme runden
  }
}

function BackBar({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="flex items-center gap-2.5">
      <button
        type="button"
        onClick={onBack}
        aria-label={texts.game.backToHome}
        className="grid h-10 w-10 place-items-center rounded-[10px] bg-text-2/10 text-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold focus-visible:outline-offset-2"
      >
        ←
      </button>
      <h2 className="font-display text-[1.2rem] font-bold">{title}</h2>
    </div>
  )
}

function Game() {
  const { gameId } = useParams()
  const navigate = useNavigate()
  const { showToast } = useToast()

  const [game, setGame] = useState<GameApi | null>(null)
  const [loading, setLoading] = useState(true)
  const lastSnapshot = useRef('')

  const [view, setView] = useState<View>('panel')
  const [draft, setDraft] = useState<RoundDraft | null>(null)
  const [editingRoundId, setEditingRoundId] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [confirmUndo, setConfirmUndo] = useState(false)
  const [confirmFinish, setConfirmFinish] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const online = useOnline()
  const currentUserId = parseInt(localStorage.getItem('userId') || '0', 10)

  const loadGame = useCallback(async (): Promise<GameApi | null> => {
    if (!gameId) return null
    try {
      const response = await api.get<GameApi>(`/games/${gameId}`)
      lastSnapshot.current = JSON.stringify(response.data)
      setGame(response.data)
      return response.data
    } catch {
      navigate('/')
      return null
    }
  }, [gameId, navigate])

  // Første indlæsning
  useEffect(() => {
    loadGame().then((g) => {
      setLoading(false)
      if (g) setDraft(freshDraft(g.players, g.rounds.length))
    })
  }, [loadGame])

  // Antal runder i den lokale tilstand, til at afgøre om serveren har indhentet en kø-runde
  const localRoundCount = useRef(0)
  useEffect(() => {
    localRoundCount.current = game?.rounds.length ?? 0
  }, [game])

  // Genindlæser, mens siden er synlig, og opdaterer kun hvis noget er ændret
  useEffect(() => {
    if (!gameId) return

    const poll = async () => {
      if (document.visibilityState !== 'visible' || !navigator.onLine) return
      try {
        const response = await api.get<GameApi>(`/games/${gameId}`)
        // Serveren har endnu ikke modtaget en kørunde fra service workerens kø - vent på næste forsøg
        if (response.data.rounds.length < localRoundCount.current) return
        const snapshot = JSON.stringify(response.data)
        if (snapshot !== lastSnapshot.current) {
          lastSnapshot.current = snapshot
          setGame(response.data)
        }
      } catch {
        // Næste forsøg sker ved næste interval
      }
    }

    const timer = window.setInterval(poll, POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', poll)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', poll)
    }
  }, [gameId])

  // Hvis runden man redigerer forsvinder (fx slettet fra en anden enhed), gå tilbage til et rent panel
  useEffect(() => {
    if (editingRoundId !== null && game && !game.rounds.some((r) => r.id === editingRoundId)) {
      setEditingRoundId(null)
      setDraft(freshDraft(game.players, game.rounds.length))
    }
  }, [game, editingRoundId])

  if (loading || !game) {
    return (
      <Screen>
        <p className="text-text-2">{texts.game.loading}</p>
      </Screen>
    )
  }

  const players: GamePlayerApi[] = game.players
  const indexOf = (userId: number): number => game.players.findIndex((p) => p.userId === userId)

  const editingRound = editingRoundId !== null ? game.rounds.find((r) => r.id === editingRoundId) ?? null : null
  const roundIndex = editingRound ? editingRound.roundNumber - 1 : game.rounds.length
  const dl = dealer(players, roundIndex)
  const outIds = sittingOut(players, roundIndex)
  const active = players.filter((p) => !outIds.includes(p.userId))
  const leader = [...players].sort((a, b) => b.totalScore - a.totalScore)[0]
  const gridColsClass = players.length === 5 ? 'grid-cols-5' : players.length === 6 ? 'grid-cols-3' : 'grid-cols-4'

  const playFeedback = () => {
    if (getSettings().sound) playSaveSound()
  }

  const setBidder = (userId: number) => {
    setDraft((d) => (d ? { ...d, bidderId: userId, partnerId: d.partnerId === userId ? null : d.partnerId } : d))
  }
  const setLevel = (level: Level) => {
    setDraft((d) => (d ? { ...d, solo: null, level, tricks: Math.min(13, Math.max(level, d.tricks)) } : d))
  }
  const setSoloMode = () => {
    setDraft((d) => (d ? { ...d, solo: d.solo ?? 'Sol', partnerId: null } : d))
  }
  const setSolo = (solo: Solo) => setDraft((d) => (d ? { ...d, solo } : d))
  // Gode spilles altid i klør; almindelige kan ikke spilles i klør
  const setVariant = (variant: Variant) =>
    setDraft((d) => {
      if (!d) return d
      const trumpSuit =
        variant === 'Gode' ? 'Klør' : variant === 'Almindelige' && d.trumpSuit === 'Klør' ? 'Hjerter' : d.trumpSuit
      return { ...d, variant, trumpSuit }
    })
  const setTrump = (trumpSuit: TrumpSuit) => setDraft((d) => (d ? { ...d, trumpSuit } : d))
  const setPartner = (userId: number) => setDraft((d) => (d ? { ...d, partnerId: userId } : d))
  const setWon = (won: boolean) => setDraft((d) => (d ? { ...d, won } : d))
  const stepTricks = (delta: number) => {
    setDraft((d) => (d ? { ...d, tricks: Math.max(0, Math.min(13, d.tricks + delta)) } : d))
  }

  const startEdit = (round: RoundApi) => {
    setDraft(draftFromRound(round))
    setEditingRoundId(round.id)
    setError('')
    setConfirmUndo(false)
    setConfirmFinish(false)
    setView('panel')
  }

  const cancelEdit = () => {
    setEditingRoundId(null)
    setError('')
    setDraft(freshDraft(game.players, game.rounds.length))
  }

  // Kun nye runder køes af service workeren (workbox backgroundSync); en offline PATCH kan ikke gendannes
  const applyOfflineRound = (body: {
    dealerId: number
    bidderId: number
    partnerId?: number
    bidType: BidType
    trumpSuit?: TrumpSuit
    tricksTaken: number
    won?: boolean
  }): void => {
    setGame((g) => {
      if (!g) return g
      const dist = distribute(g.players, g.rounds.length, inputFromRound(body))
      const pendingRound: RoundApi = {
        id: -Date.now(),
        roundNumber: g.rounds.length + 1,
        ...body,
        score: dist[body.bidderId] ?? 0,
        pending: true
      }
      return {
        ...g,
        players: g.players.map((p) => ({ ...p, totalScore: p.totalScore + (dist[p.userId] ?? 0) })),
        rounds: [...g.rounds, pendingRound]
      }
    })
  }

  const handleSubmit = async () => {
    if (!draft) return
    setError('')
    setSaving(true)
    const isEditing = editingRoundId !== null
    const body = {
      dealerId: dl.userId,
      bidderId: draft.bidderId,
      partnerId: draft.solo ? undefined : draft.partnerId ?? undefined,
      bidType: toBidType({ level: draft.level, variant: draft.variant, solo: draft.solo ?? undefined }),
      trumpSuit: draft.solo ? undefined : draft.trumpSuit,
      tricksTaken: draft.solo ? 0 : draft.tricks,
      won: draft.solo ? draft.won : undefined
    }

    try {
      if (isEditing) {
        await api.patch(`/games/${game.id}/rounds/${editingRoundId}`, body)
      } else {
        await api.post(`/games/${game.id}/rounds`, body)
      }
      const updated = await loadGame()
      setEditingRoundId(null)
      if (updated) setDraft(freshDraft(updated.players, updated.rounds.length))
      playFeedback()
      showToast(isEditing ? texts.game.roundUpdated : texts.game.roundSaved(updated?.rounds.length ?? roundIndex + 1))
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err: any) {
      if (!navigator.onLine && !isEditing) {
        applyOfflineRound(body)
        setDraft(freshDraft(game.players, roundIndex + 1))
        showToast(texts.pwa.savedOffline)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } else {
        setError(err.response?.data?.message || (isEditing ? texts.game.couldNotUpdateRound : texts.game.couldNotSaveRound))
      }
    } finally {
      setSaving(false)
    }
  }

  const handleUndo = async () => {
    setError('')
    try {
      await api.delete(`/games/${game.id}/rounds/last`)
      const updated = await loadGame()
      if (updated) setDraft(freshDraft(updated.players, updated.rounds.length))
    } catch (err: any) {
      setError(err.response?.data?.message || texts.game.couldNotUndo)
    } finally {
      setConfirmUndo(false)
    }
  }

  const handleFinish = async () => {
    setError('')
    try {
      await api.patch(`/games/${game.id}/complete`)
      navigate('/')
    } catch (err: any) {
      setError(err.response?.data?.message || texts.game.couldNotFinish)
      setConfirmFinish(false)
    }
  }

  const handleDelete = async () => {
    setError('')
    try {
      await api.delete(`/games/${game.id}`)
      showToast(texts.game.gameDeleted)
      navigate('/spil')
    } catch (err: any) {
      setError(err.response?.data?.message || texts.game.couldNotDelete)
      setConfirmDelete(false)
    }
  }

  let previewNode: ReactNode = null
  if (draft) {
    if (!draft.solo && draft.partnerId === null) {
      previewNode = <span className="text-text-2">{texts.game.choosePartner}</span>
    } else {
      const input = draftToInput(draft)
      const dist = distribute(players, roundIndex, input)
      const isSelfPartner = !draft.solo && draft.partnerId === draft.bidderId
      const team = draft.solo || isSelfPartner ? [draft.bidderId] : [draft.bidderId, draft.partnerId as number]
      const teamNames = team.map((id) => players.find((p) => p.userId === id)?.name ?? '').join(' + ')
      const bidderScore = dist[draft.bidderId] ?? 0
      const otherPlayer = active.find((p) => !team.includes(p.userId))
      const otherScore = otherPlayer ? dist[otherPlayer.userId] ?? 0 : 0
      previewNode = (
        <>
          {teamNames}
          {isSelfPartner ? texts.game.selfNote : ''} <b className={cls(bidderScore)}>{fmt(bidderScore)}</b> ·{' '}
          {texts.game.others} <b className={cls(otherScore)}>{fmt(otherScore)}</b>
        </>
      )
    }
  }

  const saveDisabled = !draft || saving || (!draft.solo && draft.partnerId === null)
  const lastRound = game.rounds[game.rounds.length - 1]

  return (
    <Screen bare={view === 'board'}>
      {view === 'panel' && (
        <>
          <div className="flex items-start justify-between gap-2">
            <div>
              <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">{game.name}</span>
              <h2>
                {editingRound ? texts.game.editRound(roundIndex + 1) : texts.game.round(roundIndex + 1)}{' '}
                <span className="text-[0.8em] font-normal text-text-2">· {texts.game.dealerGives(dl.name)}{outIds.length > 0 && ` · ${texts.game.sittingOutNames(game.players.filter((p) => outIds.includes(p.userId)).map((p) => p.name))}`}</span>
              </h2>
            </div>
            <div className="flex gap-1.5">
              <Button variant="ghost" size="sm" onClick={() => setView('board')}>
                {texts.game.show}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setView('history')}>
                {texts.game.history}
              </Button>
            </div>
          </div>
          {!online && <Chip variant="gold">{texts.pwa.offlineChip}</Chip>}

          <div className={`grid gap-1.5 ${gridColsClass}`}>
            {game.players.map((player, index) => (
              <div
                key={player.userId}
                className={`relative grid justify-items-center gap-0.5 rounded-xl border p-2 text-center ${
                  leader?.userId === player.userId ? 'border-gold' : 'border-text-2/20'
                } bg-text-2/10 ${outIds.includes(player.userId) ? 'border-dashed' : ''}`}
              >
                {dl.userId === player.userId && (
                  <span className="absolute -top-1.5 -right-1 rounded-full bg-gold px-1.5 py-px text-[0.65rem] font-extrabold text-ink">
                    {texts.game.dealerBadge}
                  </span>
                )}
                <Avatar name={player.name} index={index} />
                <span className="max-w-full truncate text-[0.78em] text-text-2">{player.name}</span>
                <span className={`font-mono text-[1.1em] font-semibold ${cls(player.totalScore)}`}>
                  {fmt(player.totalScore)}
                </span>
                {outIds.includes(player.userId) && (
                  <span className="rounded-full bg-red/20 px-1.5 py-px text-[0.68rem] font-bold text-red">{texts.game.sittingOut}</span>
                )}
              </div>
            ))}
          </div>

          {game.status === 'active' && draft && (
            <Panel>
              <div className="grid gap-1.5">
                <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">
                  {texts.game.bidderLabel}
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {active.map((p) => (
                    <button
                      key={p.userId}
                      type="button"
                      onClick={() => setBidder(p.userId)}
                      className={`grid min-w-[60px] justify-items-center gap-1 rounded-xl px-1 py-1.5 text-[0.8em] font-semibold ${
                        draft.bidderId === p.userId ? 'bg-text-2/20 text-text' : 'bg-ink/20 text-text-2 opacity-55'
                      }`}
                    >
                      <Avatar
                        name={p.name}
                        index={indexOf(p.userId)}
                        size="lg"
                        className={draft.bidderId === p.userId ? 'border-2 border-gold' : ''}
                      />
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-1">
                {LEVELS.map((level) => (
                  <button
                    key={level}
                    type="button"
                    onClick={() => setLevel(level)}
                    className={`min-h-11 flex-1 rounded-[10px] font-mono font-semibold ${
                      !draft.solo && draft.level === level ? 'bg-cream text-ink' : 'bg-ink/25 text-text-2'
                    }`}
                  >
                    {level}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={setSoloMode}
                  className={`min-h-11 flex-[1.4] rounded-[10px] font-semibold ${
                    draft.solo ? 'bg-cream text-ink' : 'bg-ink/25 text-text-2'
                  }`}
                >
                  {texts.game.soloLabel}
                </button>
              </div>

              {draft.solo ? (
                <>
                  <div className="flex gap-1 rounded-xl bg-ink/25 p-1">
                    {SOLO.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSolo(s)}
                        className={`min-h-11 flex-1 rounded-[9px] text-[0.85em] font-semibold ${
                          draft.solo === s ? 'bg-cream text-ink' : 'text-text-2'
                        }`}
                      >
                        {s.replace('bordlægger', 'bordl.')}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-1 rounded-xl bg-ink/25 p-1">
                    <button
                      type="button"
                      onClick={() => setWon(true)}
                      className={`min-h-11 flex-1 rounded-[9px] text-[0.85em] font-semibold ${
                        draft.won ? 'bg-cream text-ink' : 'text-text-2'
                      }`}
                    >
                      {texts.game.won}
                    </button>
                    <button
                      type="button"
                      onClick={() => setWon(false)}
                      className={`min-h-11 flex-1 rounded-[9px] text-[0.85em] font-semibold ${
                        !draft.won ? 'bg-cream text-ink' : 'text-text-2'
                      }`}
                    >
                      {texts.game.lost}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex gap-1 rounded-xl bg-ink/25 p-1">
                    {VARIANTS.map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setVariant(v)}
                        className={`min-h-11 flex-1 rounded-[9px] text-[0.85em] font-semibold ${
                          draft.variant === v ? 'bg-cream text-ink' : 'text-text-2'
                        }`}
                      >
                        {v.replace('Almindelige', 'Alm.')}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-1.5">
                    {SUITS.map(([suit, sym, color]) => {
                      const isOn = draft.trumpSuit === suit
                      const locked =
                        (draft.variant === 'Gode' && suit !== 'Klør') ||
                        (draft.variant === 'Almindelige' && suit === 'Klør')
                      return (
                        <button
                          key={suit}
                          type="button"
                          disabled={locked}
                          onClick={() => setTrump(suit)}
                          className={`min-h-11 flex-1 rounded-[10px] text-[1.3em] disabled:opacity-25 ${
                            isOn ? (color === 'r' ? 'bg-cream text-[#e5323e]' : 'bg-cream text-ink') : 'bg-ink/25'
                          } ${!isOn && color === 'r' ? 'text-red' : ''} ${
                            suit === 'Trumfløs' ? 'whitespace-nowrap text-[0.62em] font-semibold' : ''
                          }`}
                        >
                          {sym}
                        </button>
                      )
                    })}
                  </div>
                  <div className="grid gap-1.5">
                    <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">
                      {texts.game.partnerLabel}
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {active
                        .filter((p) => p.userId !== draft.bidderId)
                        .map((p) => (
                          <button
                            key={p.userId}
                            type="button"
                            onClick={() => setPartner(p.userId)}
                            className={`grid min-w-[60px] justify-items-center gap-1 rounded-xl px-1 py-1.5 text-[0.8em] font-semibold ${
                              draft.partnerId === p.userId ? 'bg-text-2/20 text-text' : 'bg-ink/20 text-text-2 opacity-55'
                            }`}
                          >
                            <Avatar
                              name={p.name}
                              index={indexOf(p.userId)}
                              size="lg"
                              className={draft.partnerId === p.userId ? 'border-2 border-gold' : ''}
                            />
                            {p.name}
                          </button>
                        ))}
                      <button
                        type="button"
                        onClick={() => setPartner(draft.bidderId)}
                        className={`grid min-w-[60px] justify-items-center gap-1 rounded-xl px-1 py-1.5 text-[0.8em] font-semibold ${
                          draft.partnerId === draft.bidderId ? 'bg-text-2/20 text-text' : 'bg-ink/20 text-text-2 opacity-55'
                        }`}
                      >
                        <span className="inline-grid h-[calc(44px*var(--scale))] w-[calc(44px*var(--scale))] place-items-center rounded-full bg-text-2/20 text-text">
                          ×3
                        </span>
                        {texts.game.selfLabel}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-ink/25 p-1.5">
                    <button
                      type="button"
                      aria-label={texts.game.fewerTricks}
                      onClick={() => stepTricks(-1)}
                      className="grid h-12 w-12 place-items-center rounded-[10px] bg-text-2/20 text-2xl"
                    >
                      −
                    </button>
                    <div className="text-center">
                      <b className="block font-mono text-[1.6em]">{draft.tricks}</b>
                      <small className="block text-[0.7rem] text-text-2">{texts.game.tricksTakenLabel}</small>
                    </div>
                    <button
                      type="button"
                      aria-label={texts.game.moreTricks}
                      onClick={() => stepTricks(1)}
                      className="grid h-12 w-12 place-items-center rounded-[10px] bg-text-2/20 text-2xl"
                    >
                      +
                    </button>
                  </div>
                </>
              )}

              <p className="min-h-[1.4em] text-center text-[0.95em] text-text-2">{previewNode}</p>
            </Panel>
          )}

          {game.status === 'active' && draft && (
            <div className="grid gap-2">
              <Button full disabled={saveDisabled} onClick={handleSubmit}>
                {editingRoundId !== null ? texts.game.updateRound : texts.game.saveRound}
              </Button>
              {error && <p className="text-[0.9em] font-medium text-red">{error}</p>}

              {editingRoundId !== null ? (
                <Button variant="ghost" full onClick={cancelEdit}>
                  {texts.game.cancel}
                </Button>
              ) : (
                <>
                  {confirmUndo && (
                    <div className="grid gap-2 rounded-xl border border-red/40 bg-red/10 p-3">
                      <p className="text-[0.85em] text-text">{texts.game.confirmUndo}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Button variant="ghost" onClick={() => setConfirmUndo(false)}>
                          {texts.game.cancel}
                        </Button>
                        <Button variant="danger" onClick={handleUndo}>
                          {texts.game.confirmUndoButton}
                        </Button>
                      </div>
                    </div>
                  )}
                  {confirmFinish && (
                    <div className="grid gap-2 rounded-xl border border-gold/40 bg-gold/10 p-3">
                      <p className="text-[0.85em] text-text">{texts.game.confirmFinish}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Button variant="ghost" onClick={() => setConfirmFinish(false)}>
                          {texts.game.cancel}
                        </Button>
                        <Button onClick={handleFinish}>{texts.game.confirmFinishButton}</Button>
                      </div>
                    </div>
                  )}
                  {confirmDelete && (
                    <div className="grid gap-2 rounded-xl border border-red/40 bg-red/10 p-3">
                      <p className="text-[0.85em] text-text">{texts.game.deleteConfirm}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
                          {texts.game.deleteConfirmNo}
                        </Button>
                        <Button variant="danger" onClick={handleDelete}>
                          {texts.game.deleteConfirmYes}
                        </Button>
                      </div>
                    </div>
                  )}
                  {!confirmUndo && !confirmFinish && !confirmDelete && (
                    <>
                      {game.rounds.length > 0 && (
                        <div className="grid grid-cols-2 gap-2">
                          <Button variant="ghost" onClick={() => setConfirmUndo(true)}>
                            {texts.game.undoLast}
                          </Button>
                          <Button variant="ghost" onClick={() => setConfirmFinish(true)}>
                            {texts.game.finishGame}
                          </Button>
                        </div>
                      )}
                      {game.createdBy === currentUserId && (
                        <Button variant="danger" full onClick={() => setConfirmDelete(true)}>
                          {texts.game.deleteGame}
                        </Button>
                      )}
                      {game.rounds.length === 0 && (
                        <p className="text-center text-[0.85em] text-text-2">{texts.game.deleteHint}</p>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {game.status === 'completed' && (
            <Panel>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[0.72rem] uppercase tracking-[0.08em] text-text-2">
                  {texts.game.finalStanding}
                </span>
                <Chip variant="gold">{texts.game.completed}</Chip>
              </div>
              <div className="flex flex-wrap gap-2">
                <ShareButton gameId={game.id} />
                <CsvButton href={gameExportCsvUrl(game.id)} label={shareTexts.share.csvGameLabel} />
              </div>
              <div className="grid gap-1.5">
                {[...players]
                  .sort((a, b) => b.totalScore - a.totalScore)
                  .map((p) => (
                    <div key={p.userId} className="flex items-center justify-between rounded-xl bg-ink/20 px-3 py-2">
                      <span className="flex items-center gap-2">
                        <Avatar name={p.name} index={indexOf(p.userId)} />
                        {p.name}
                      </span>
                      <span className={`font-mono font-semibold ${cls(p.totalScore)}`}>{fmt(p.totalScore)}</span>
                    </div>
                  ))}
              </div>
            </Panel>
          )}

          <BottomNav />
        </>
      )}

      {view === 'history' && (
        <>
          <BackBar title={texts.game.history} onBack={() => setView('panel')} />
          <p className="text-[0.85em] text-text-2">
            {game.name} · {texts.game.historyHint(game.rounds.length)}
          </p>
          <div className="flex flex-wrap gap-2">
            <ShareButton gameId={game.id} />
            <CsvButton href={gameExportCsvUrl(game.id)} label={shareTexts.share.csvGameLabel} />
          </div>
          {game.rounds.length >= 2 && (
            <Panel>
              <GameCurve game={{ players, rounds: game.rounds }} ariaLabel={texts.game.curveLabel} />
            </Panel>
          )}
          <div className="grid gap-2.5">
            {game.rounds.length === 0 ? (
              <p className="text-text-2">{texts.game.noRoundsYet}</p>
            ) : (
              (() => {
                // Løbende sum pr. spiller, runde for runde, til "total efter denne runde"
                const cumulative: Record<number, number> = {}
                players.forEach((p) => {
                  cumulative[p.userId] = 0
                })
                const withTotals = game.rounds.map((round) => {
                  const idx = round.roundNumber - 1
                  const dist = distribute(players, idx, inputFromRound(round))
                  players.forEach((p) => {
                    cumulative[p.userId] += dist[p.userId] ?? 0
                  })
                  return { round, dist, totals: { ...cumulative } }
                })
                return [...withTotals].reverse().map(({ round, dist, totals }) => {
                  const isSolo = (SOLO as string[]).includes(round.bidType)
                  const isSelfPartner = !isSolo && round.partnerId === round.bidderId
                  const bidderName = players.find((p) => p.userId === round.bidderId)?.name ?? ''
                  const partnerName =
                    !isSolo && !isSelfPartner && round.partnerId
                      ? players.find((p) => p.userId === round.partnerId)?.name ?? ''
                      : ''
                  const detail = isSolo
                    ? ' · ' + (round.won ? texts.game.won.toLowerCase() : texts.game.lost.toLowerCase())
                    : ` i ${(round.trumpSuit ?? '').toLowerCase()} · ${round.tricksTaken} ${texts.game.tricksSuffix}`
                  const header = `${texts.game.round(round.roundNumber)} · ${bidderName}${
                    partnerName ? ' + ' + partnerName : ''
                  }${isSelfPartner ? texts.game.selfNote : ''} · ${round.bidType}${detail}`
                  return (
                    <Panel key={round.id}>
                      <button
                        type="button"
                        onClick={() => startEdit(round)}
                        className="flex items-start justify-between gap-2 text-left text-inherit"
                      >
                        <b className="min-w-0">{header}</b>
                        {round.pending && <Chip variant="gold">{texts.pwa.pendingChip}</Chip>}
                      </button>
                      <div className="grid gap-1">
                        <div className="grid grid-cols-[1fr_auto_auto] gap-2.5 text-[0.68rem] uppercase tracking-[0.06em] text-text-2/70">
                          <span />
                          <span className="w-12 text-right">{texts.game.roundPointsLabel}</span>
                          <span className="w-14 text-right">{texts.game.roundTotalAfter}</span>
                        </div>
                        {players.map((p) => (
                          <div key={p.userId} className="grid grid-cols-[1fr_auto_auto] items-center gap-2.5 text-[0.85em]">
                            <span className="min-w-0 truncate">{p.name}</span>
                            <span className={`w-12 text-right font-mono ${cls(dist[p.userId] ?? 0)}`}>
                              {fmt(dist[p.userId] ?? 0)}
                            </span>
                            <span className={`w-14 text-right font-mono font-semibold ${cls(totals[p.userId] ?? 0)}`}>
                              {fmt(totals[p.userId] ?? 0)}
                            </span>
                          </div>
                        ))}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => startEdit(round)}>
                        {texts.game.editRoundButton}
                      </Button>
                    </Panel>
                  )
                })
              })()
            )}
          </div>
          <BottomNav />
        </>
      )}

      {view === 'board' && (
        <>
          <div className="flex items-center justify-between gap-2.5">
            <BackBar title={game.name} onBack={() => setView('panel')} />
          </div>
          <Chip variant="gold">
            {texts.game.round(roundIndex + 1)} · {texts.game.dealerGives(dl.name)}
          </Chip>
          <div className="grid gap-2.5">
            {[...players]
              .sort((a, b) => b.totalScore - a.totalScore)
              .map((p) => (
                <div
                  key={p.userId}
                  className="grid grid-cols-[44px_1fr_auto] items-center gap-3.5 rounded-2xl bg-text-2/10 px-4.5 py-3.5 text-[1.5em]"
                >
                  <Avatar name={p.name} index={indexOf(p.userId)} />
                  <span>{p.name}</span>
                  <span className={`font-mono text-[1.4em] font-bold ${cls(p.totalScore)}`}>{fmt(p.totalScore)}</span>
                </div>
              ))}
          </div>
          {lastRound && (
            <p className="text-center text-text-2">
              {texts.game.lastRound(
                `${players.find((p) => p.userId === lastRound.bidderId)?.name ?? ''} ${lastRound.bidType}${
                  (SOLO as string[]).includes(lastRound.bidType) ? '' : `, ${lastRound.tricksTaken} ${texts.game.tricksTakenLabel}`
                }`
              )}
            </p>
          )}
          <p className="text-center text-[0.85em] text-text-2">{texts.game.boardHint}</p>
        </>
      )}
    </Screen>
  )
}

export default Game
